import { Component, EventEmitter, OnInit, OnDestroy, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin, Subscription } from 'rxjs';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { QreEntityDetailsService } from './qre-entity-details.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  firstGridRowObjectFromPayload,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  sanitizeActionParams
} from '../../../shared/workspace-element-context';

type TabSuffix = 'CY' | 'BY1' | 'BY2' | 'BY3';

interface QreSummary {
  CURRENT_YEAR_CQRE: number;
  CURRENT_one_QRE: number;
  CURRENT_two_QRE: number;
  CURRENT_three_QRE: number;
  CURRENT_TOTAL_QRE: number;
  CURRENT_YEAR_CQRE_Y: number;
  CURRENT_one_QRE_Y: number;
  CURRENT_two_QRE_Y: number;
  CURRENT_three_QRE_Y: number;
  CURRENT_TOTAL_QRE_Y: number;
}

@Component({
    selector: 'gtw-qre-entity-details',
    templateUrl: './qre-entity-details.component.html',
    styleUrls: ['./qre-entity-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class QreEntityDetailsComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();
  private isInitialized = false;

  baseURLs: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};

  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = true;
  noRecords = false;

  modalTitle = 'CY/BY QRE - R&D Details';
  activeScheduleTab = 0;
  selectedTab = 0;
  hideCopy = true;
  copyValue = '';

  yearTabs: string[] = [];
  templateData: string[] = [];
  summaryCol2 = '';
  summaryCol3 = '';
  summaryCol4 = '';
  summaryCol5 = '';
  totalHeaderTab = '';
  totalApplicableHeaderTab = '';
  tab3Title = '';
  tab4Title1 = '';
  tab4Title2 = '';

  summary: QreSummary = this.createEmptySummary();

  lineOptions: any[] = [];
  lineDescriptionOptions: string[] = [];
  stateOptions: any[] = [];
  stateNames: string[] = [];

  tab1Rows: any[] = [];
  tab2Rows: any[] = [];
  tab3Rows: any[] = [];
  tab4Rows: any[] = [];
  deletedRows: any[] = [];

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private qreEntityDetailsService: QreEntityDetailsService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => { this.applyWorkspaceContext(); this.tryInit(); }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => { this.applyWorkspaceContext(); this.tryInit(); }));
    this.tryInit();
  }

  ngOnDestroy(): void { this.subs.unsubscribe(); }

  get entityDisplay(): string {
    const r = this.rowData || {};
    return `${r.CY_LEID || r.LEID || ''} - ${r.CY_TAX_CODE || r.TAX_CODE || ''} - ${r.LE_NAME || r.CY_LE_NAME || ''}`;
  }

  get businessDisplay(): string {
    const r = this.rowData || {};
    return `${r.CY_ME_CODE || r.ME_CODE || ''} - ${r.ME_NAME || ''}`;
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = header;
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = firstGridRowObjectFromPayload(rawRow);
    this.cdr.detectChanges();
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    if (!this.GENERAL_CONFIG || !this.rowData || Object.keys(this.rowData).length === 0) return;
    this.isInitialized = true;
    this.init();
  }

  private init(): void {
    this.initLabels();
    this.hideCopy = true;
    this.copyValue = '';
    this.selectedTab = 0;
    this.loadData();
  }

  private initLabels(): void {
    const taxYear = Number(this.filterParams?.tax_year || this.globalService?.globalParams?.tax_year);
    const year = Number.isFinite(taxYear) && taxYear > 0 ? taxYear : new Date().getFullYear();

    this.yearTabs = [
      `${year} - Current Year`,
      `${year - 1} - Base Year`,
      `${year - 2} - Base Year`,
      `${year - 3} - Base Year`
    ];
    this.summaryCol2 = `${year} - Current Year QRE`;
    this.summaryCol3 = `${year - 1} - Base Year QRE`;
    this.summaryCol4 = `${year - 2} - Base Year QRE`;
    this.summaryCol5 = `${year - 3} - Base Year QRE`;
    this.totalHeaderTab = `${year} Adj Total`;
    this.totalApplicableHeaderTab = `${year} Applicable Adj Total`;
    this.tab3Title = `Adj In ${year - 1}`;
    this.tab4Title1 = `Adj In ${year - 2}`;
    this.tab4Title2 = `Adj In ${year - 1}`;
    this.updateCopyOptions();
  }

  private createEmptySummary(): QreSummary {
    return {
      CURRENT_YEAR_CQRE: 0,
      CURRENT_one_QRE: 0,
      CURRENT_two_QRE: 0,
      CURRENT_three_QRE: 0,
      CURRENT_TOTAL_QRE: 0,
      CURRENT_YEAR_CQRE_Y: 0,
      CURRENT_one_QRE_Y: 0,
      CURRENT_two_QRE_Y: 0,
      CURRENT_three_QRE_Y: 0,
      CURRENT_TOTAL_QRE_Y: 0
    };
  }

  private getLoadParams(): Record<string, any> {
    const g = this.globalService?.globalParams || {};
    const f = this.filterParams || {};
    const row = this.rowData || {};
    return sanitizeActionParams({
      tax_year: f.tax_year ?? g.tax_year ?? row.TAX_YEAR,
      scenario: f.scenario ?? g.scenario ?? row.SCENARIO,
      jcd_key: f.jcd_key ?? g.jcd_key,
      controlled_grp: f.controlled_grp ?? g.controlled_grp,
      filling_key: f.filling_key ?? g.filling_key,
      parent_me_string_modal: row.ME_KEY,
      combi_key: row.CY_COMBINATION_KEY,
      ...g
    });
  }

  private loadData(): void {
    this.showFormLoader = true;
    this.cdr.detectChanges();
    const params = this.getLoadParams();
    const baseUrl = this.GENERAL_CONFIG.base_url;

    forkJoin({
      stateData: this.qreEntityDetailsService.loadData(baseUrl, 'g9d8eo', params),
      lineData: this.qreEntityDetailsService.loadData(baseUrl, 'xkk1gy', params),
      reclassData: this.qreEntityDetailsService.loadData(baseUrl, 'rvrjpf', params)
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      ({ stateData, lineData, reclassData }) => {
        this.processStateData(stateData);
        this.processLineData(lineData);
        this.processReclassData(reclassData);
        this.showFormLoader = false;
        this.cdr.detectChanges();
      },
      () => {
        this.showFormLoader = false;
        this.cdr.detectChanges();
      }
    );
  }

  private processStateData(response: any): void {
    this.stateOptions = Array.isArray(response?.jsonObject) ? response.jsonObject : [];
    this.stateNames = this.stateOptions.map((item: any) => item?.STATE_NAME).filter((name: string) => !!name);
  }

  private processLineData(response: any): void {
    this.lineOptions = Array.isArray(response?.jsonObject) ? response.jsonObject : [];
    this.lineDescriptionOptions = this.lineOptions.map((item: any) => item?.LINE_DESCRIPTION).filter((name: string) => !!name);
  }

  private processReclassData(response: any): void {
    const rows = Array.isArray(response?.jsonObject) ? response.jsonObject : [];
    this.tab1Rows = [];
    this.tab2Rows = [];
    this.tab3Rows = [];
    this.tab4Rows = [];

    rows.forEach((row: any) => {
      if (row?.TAB_YEAR === 'CY') this.tab1Rows.push({ ...row });
      if (row?.TAB_YEAR === 'BY1') this.tab2Rows.push({ ...row });
      if (row?.TAB_YEAR === 'BY2') this.tab3Rows.push({ ...row });
      if (row?.TAB_YEAR === 'BY3') this.tab4Rows.push({ ...row });
    });

    this.normalizeLoadedRows();
    this.recalculateSummary();
    this.noRecords = !this.tab1Rows.length && !this.tab2Rows.length && !this.tab3Rows.length && !this.tab4Rows.length;
  }

  private normalizeLoadedRows(): void {
    this.tab1Rows.forEach((row: any) => this.normalizeTab1Row(row, false));
    this.tab2Rows.forEach((row: any) => this.normalizeTab2Row(row, false));
    this.tab3Rows.forEach((row: any) => this.normalizeTab3Row(row, false));
    this.tab4Rows.forEach((row: any) => this.normalizeTab4Row(row, false));
  }

  private normalizeTab1Row(row: any, isNew: boolean): void {
    row.display_dropdown = isNew;
    row.INCLUDE_IN_CALC_CY = row.INCLUDE_IN_CALC || '';
    row.STATE_CY = row.STATE_NAME || row.STATE_CY || '';
    row.APPLICABLE_PERCENT_CY = Number(row.APPLICABLE_PERCENT || row.APPLICABLE_PERCENT_CY || 0);
    row.LINE_DESC_CY = row.LINE_DESC || row.LINE_DESC_CY || '';
    row.RAF_CY = Number(row.RAF || row.RAF_CY || 0);
    row.TAB_YEAR_CY = 'CY';
    row.MAP_KEY_CY = row.MAP_KEY || '';
    row.business_component = row.BUSINESS_COMPONENT ?? row.business_component ?? row.bus_component ?? '';
    row.bus_component = row.business_component;
    row.BUSINESS_COMPONENT = row.business_component;
    row.bc_type = row.BC_TYPE ?? row.bc_type ?? '';
    row.BC_TYPE = row.bc_type;
    row.software_type = row.SOFTWARE_TYPE ?? row.software_type ?? '';
    row.SOFTWARE_TYPE = row.software_type;
    row.ADJ_CY_2_CY = Number(row.ADJ_CY_2 || row.ADJ_CY_2_CY || 0);
    row.ADJ_CY_1_CY = Number(row.ADJ_CY_1 || row.ADJ_CY_1_CY || 0);
    row.ADJ_CY_EXAM_CY = Number(row.ADJ_CY_EXAM || row.ADJ_CY_EXAM_CY || 0);
    row.ADJ_CY_CONSITENCY_CY = Number(row.ADJ_CY_CONSITENCY || row.ADJ_CY_CONSITENCY_CY || 0);
    row.ADJ_CY_ACQUISITION_CY = Number(row.ADJ_CY_ACQUISITION || row.ADJ_CY_ACQUISITION_CY || 0);
    row.ADJ_CY_DISPOSITION_CY = Number(row.ADJ_CY_DISPOSITION || row.ADJ_CY_DISPOSITION_CY || 0);
    row.ADJ_TOTAL_CY = (row.RAF_CY * row.APPLICABLE_PERCENT_CY) / 100;
    row.ADJ_TOTAL_APPLICABLE_CY = row.ADJ_TOTAL_CY;
    this.stampTab1LegacyFields(row, isNew);
  }

  private normalizeTab2Row(row: any, isNew: boolean): void {
    row.display_dropdown_tab2 = isNew;
    row.open = false;
    row.INCLUDE_IN_CALC_BY1 = row.INCLUDE_IN_CALC || '';
    row.LINE_DESC_BY1 = row.LINE_DESC || row.LINE_DESC_BY1 || '';
    row.APPLICABLE_PERCENT_BY1 = Number(row.APPLICABLE_PERCENT || row.APPLICABLE_PERCENT_BY1 || 0);
    row.RAF_BY1 = Number(row.RAF || row.RAF_BY1 || 0);
    row.ADJ_CY_EXAM_BY1 = Number(row.ADJ_CY_EXAM || row.ADJ_CY_EXAM_BY1 || 0);
    row.ADJ_CY_CONSITENCY_BY1 = Number(row.ADJ_CY_CONSITENCY || row.ADJ_CY_CONSITENCY_BY1 || 0);
    row.ADJ_CY_ACQUISITION_BY1 = Number(row.ADJ_CY_ACQUISITION || row.ADJ_CY_ACQUISITION_BY1 || 0);
    row.ADJ_CY_DISPOSITION_BY1 = Number(row.ADJ_CY_DISPOSITION || row.ADJ_CY_DISPOSITION_BY1 || 0);
    row.ADJ_CY_ANNUALIZATION_BY1 = Number(row.ADJ_CY_ANNUALIZATION || row.ADJ_CY_ANNUALIZATION_BY1 || 0);
    row.ADJ_CY_PTP_BY1 = Number(row.ADJ_CY_PTP || row.ADJ_CY_PTP_BY1 || 0);
    row.STATE_BY1 = row.STATE_NAME || row.STATE_BY1 || '';
    row.TAB_YEAR_BY1 = 'BY1';
    row.MAP_KEY_BY1 = row.MAP_KEY || '';
    row.ADJ_CY_EXAM_D_BY1 = row.ADJ_DESC_CY_EXAM || row.ADJ_CY_EXAM_D_BY1 || '';
    row.ADJ_CY_CONSITENCY_D_BY1 = row.ADJ_DESC_CY_CONSITENCY || row.ADJ_CY_CONSITENCY_D_BY1 || '';
    row.ADJ_CY_ACQUISITION_D_BY1 = row.ADJ_DESC_CY_ACQUISITION || row.ADJ_CY_ACQUISITION_D_BY1 || '';
    row.ADJ_CY_DISPOSITION_D_BY1 = row.ADJ_DESC_CY_DISPOSITION || row.ADJ_CY_DISPOSITION_D_BY1 || '';
    row.ADJ_CY_ANNUALIZATION_D_BY1 = row.ADJ_DESC_CY_ANNUALIZATION || row.ADJ_CY_ANNUALIZATION_D_BY1 || '';
    this.recalculateBy1Row(row);
    this.stampTab2LegacyFields(row, isNew);
  }

  private normalizeTab3Row(row: any, isNew: boolean): void {
    row.display_dropdown_tab3 = isNew;
    row.open = false;
    row.INCLUDE_IN_CALC_BY2 = row.INCLUDE_IN_CALC || '';
    row.LINE_DESC_BY2 = row.LINE_DESC || row.LINE_DESC_BY2 || '';
    row.APPLICABLE_PERCENT_BY2 = Number(row.APPLICABLE_PERCENT || row.APPLICABLE_PERCENT_BY2 || 0);
    row.RAF_BY2 = Number(row.RAF || row.RAF_BY2 || 0);
    row.ADJ_CY_1_BY2 = Number(row.ADJ_CY_1 || row.ADJ_CY_1_BY2 || 0);
    row.ADJ_CY_EXAM_BY2 = Number(row.ADJ_CY_EXAM || row.ADJ_CY_EXAM_BY2 || 0);
    row.ADJ_CY_CONSITENCY_BY2 = Number(row.ADJ_CY_CONSITENCY || row.ADJ_CY_CONSITENCY_BY2 || 0);
    row.ADJ_CY_ACQUISITION_BY2 = Number(row.ADJ_CY_ACQUISITION || row.ADJ_CY_ACQUISITION_BY2 || 0);
    row.ADJ_CY_DISPOSITION_BY2 = Number(row.ADJ_CY_DISPOSITION || row.ADJ_CY_DISPOSITION_BY2 || 0);
    row.ADJ_CY_ANNUALIZATION_BY2 = Number(row.ADJ_CY_ANNUALIZATION || row.ADJ_CY_ANNUALIZATION_BY2 || 0);
    row.ADJ_CY_PTP_BY2 = Number(row.ADJ_CY_PTP || row.ADJ_CY_PTP_BY2 || 0);
    row.STATE_BY2 = row.STATE_NAME || row.STATE_BY2 || '';
    row.TAB_YEAR_BY2 = 'BY2';
    row.MAP_KEY_BY2 = row.MAP_KEY || '';
    row.ADJ_CY_EXAM_D_BY2 = row.ADJ_DESC_CY_EXAM || row.ADJ_CY_EXAM_D_BY2 || '';
    row.ADJ_CY_CONSITENCY_D_BY2 = row.ADJ_DESC_CY_CONSITENCY || row.ADJ_CY_CONSITENCY_D_BY2 || '';
    row.ADJ_CY_ACQUISITION_D_BY2 = row.ADJ_DESC_CY_ACQUISITION || row.ADJ_CY_ACQUISITION_D_BY2 || '';
    row.ADJ_CY_DISPOSITION_D_BY2 = row.ADJ_DESC_CY_DISPOSITION || row.ADJ_CY_DISPOSITION_D_BY2 || '';
    row.ADJ_CY_ANNUALIZATION_D_BY2 = row.ADJ_DESC_CY_ANNUALIZATION || row.ADJ_CY_ANNUALIZATION_D_BY2 || '';
    this.recalculateBy2Row(row);
    this.stampTab3LegacyFields(row, isNew);
  }

  private normalizeTab4Row(row: any, isNew: boolean): void {
    row.display_dropdown_tab4 = isNew;
    row.open = false;
    row.INCLUDE_IN_CALC_BY3 = row.INCLUDE_IN_CALC || '';
    row.LINE_DESC_BY3 = row.LINE_DESC || row.LINE_DESC_BY3 || '';
    row.APPLICABLE_PERCENT_BY3 = Number(row.APPLICABLE_PERCENT || row.APPLICABLE_PERCENT_BY3 || 0);
    row.RAF_BY3 = Number(row.RAF || row.RAF_BY3 || 0);
    row.ADJ_CY_1_BY3 = Number(row.ADJ_CY_1 || row.ADJ_CY_1_BY3 || 0);
    row.ADJ_CY_2_BY3 = Number(row.ADJ_CY_2 || row.ADJ_CY_2_BY3 || 0);
    row.ADJ_CY_EXAM_BY3 = Number(row.ADJ_CY_EXAM || row.ADJ_CY_EXAM_BY3 || 0);
    row.ADJ_CY_CONSITENCY_BY3 = Number(row.ADJ_CY_CONSITENCY || row.ADJ_CY_CONSITENCY_BY3 || 0);
    row.ADJ_CY_ACQUISITION_BY3 = Number(row.ADJ_CY_ACQUISITION || row.ADJ_CY_ACQUISITION_BY3 || 0);
    row.ADJ_CY_DISPOSITION_BY3 = Number(row.ADJ_CY_DISPOSITION || row.ADJ_CY_DISPOSITION_BY3 || 0);
    row.ADJ_CY_ANNUALIZATION_BY3 = Number(row.ADJ_CY_ANNUALIZATION || row.ADJ_CY_ANNUALIZATION_BY3 || 0);
    row.ADJ_CY_PTP_BY3 = Number(row.ADJ_CY_PTP || row.ADJ_CY_PTP_BY3 || 0);
    row.STATE_BY3 = row.STATE_NAME || row.STATE_BY3 || '';
    row.TAB_YEAR_BY3 = 'BY3';
    row.MAP_KEY_BY3 = row.MAP_KEY || '';
    row.ADJ_CY_EXAM_D_BY3 = row.ADJ_DESC_CY_EXAM || row.ADJ_CY_EXAM_D_BY3 || '';
    row.ADJ_CY_CONSITENCY_D_BY3 = row.ADJ_DESC_CY_CONSITENCY || row.ADJ_CY_CONSITENCY_D_BY3 || '';
    row.ADJ_CY_ACQUISITION_D_BY3 = row.ADJ_DESC_CY_ACQUISITION || row.ADJ_CY_ACQUISITION_D_BY3 || '';
    row.ADJ_CY_DISPOSITION_D_BY3 = row.ADJ_DESC_CY_DISPOSITION || row.ADJ_CY_DISPOSITION_D_BY3 || '';
    row.ADJ_CY_ANNUALIZATION_D_BY3 = row.ADJ_DESC_CY_ANNUALIZATION || row.ADJ_CY_ANNUALIZATION_D_BY3 || '';
    this.recalculateBy3Row(row);
    this.stampTab4LegacyFields(row, isNew);
  }

  private stampTab1LegacyFields(row: any, isNew: boolean): void {
    if (isNew) {
      row.display_dropdown_state = true;
      row.old_applicable_percent_cy = 0;
      row.old_raf = 0;
      row.old_adj_cy_2 = 0;
      row.old_adj_cy_1 = 0;
      row.old_adj_cy_exam = 0;
      row.old_adj_cy_consitency = 0;
      row.old_adj_cy_acquisition = 0;
      row.old_adj_cy_disposition = 0;
      row.old_adj_total = 0;
      row.old_adj_total_applicable = 0;
      row.old_line_key = 0;
      row.old_line_desc = '';
      row.old_state = '';
      row.new_line_key = 0;
      row.new_line_desc = '';
      row.new_applicable_percent = 0;
      row.new_raf = 0;
      row.new_adj_cy_2 = 0;
      row.new_adj_cy_1 = 0;
      row.new_adj_cy_exam = 0;
      row.new_adj_cy_consitency = 0;
      row.new_adj_cy_acquisition = 0;
      row.new_adj_cy_disposition = 0;
      row.new_adj_total = 0;
      row.new_adj_total_applicable = 0;
      row.new_state = '';
      return;
    }

    row.display_dropdown_state = false;
    row.old_line_key = row.LINE_KEY;
    row.old_line_desc = row.LINE_DESC;
    row.old_applicable_percent_cy = row.APPLICABLE_PERCENT;
    row.old_raf = row.RAF;
    row.old_adj_cy_2 = row.ADJ_CY_2;
    row.old_adj_cy_1 = row.ADJ_CY_1;
    row.old_adj_cy_exam = row.ADJ_CY_EXAM;
    row.old_adj_cy_consitency = row.ADJ_CY_CONSITENCY;
    row.old_adj_cy_acquisition = row.ADJ_CY_ACQUISITION;
    row.old_adj_cy_disposition = row.ADJ_CY_DISPOSITION;
    row.old_state = row.STATE;
    row.new_state = row.STATE;
    row.new_line_key = row.LINE_KEY;
    row.new_line_desc = row.LINE_DESC;
    row.new_applicable_percent = row.APPLICABLE_PERCENT;
    row.new_raf = row.RAF;
    row.new_adj_cy_2 = row.ADJ_CY_2;
    row.new_adj_cy_1 = row.ADJ_CY_1;
    row.new_adj_cy_exam = row.ADJ_CY_EXAM;
    row.new_adj_cy_consitency = row.ADJ_CY_CONSITENCY;
    row.new_adj_cy_acquisition = row.ADJ_CY_ACQUISITION;
    row.new_adj_cy_disposition = row.ADJ_CY_DISPOSITION;
    row.old_adj_total = row.ADJ_TOTAL_CY;
    row.old_adj_total_applicable = row.ADJ_TOTAL_APPLICABLE_CY;
    row.new_adj_total = row.ADJ_TOTAL_CY;
    row.new_adj_total_applicable = row.ADJ_TOTAL_APPLICABLE_CY;
  }

  private stampTab2LegacyFields(row: any, isNew: boolean): void {
    if (isNew) {
      Object.assign(row, {
        old_applicable_percent_cy: 0, old_raf: 0, old_adj_cy_2: 0, old_adj_cy_1: 0,
        old_adj_cy_exam: 0, old_adj_cy_consitency: 0, old_adj_cy_acquisition: 0, old_adj_cy_disposition: 0,
        old_adj_total: 0, old_adj_total_applicable: 0, old_line_key: 0, old_line_desc: '',
        new_line_key: 0, new_line_desc: '', new_applicable_percent: 0, new_raf: 0,
        new_adj_cy_2: 0, new_adj_cy_1: 0, new_adj_cy_exam: 0, new_adj_cy_consitency: 0,
        new_adj_cy_acquisition: 0, new_adj_cy_disposition: 0, old_cy_ptp: 0, new_adj_cy_ptp: 0,
        new_state: '', old_state: ''
      });
      return;
    }

    row.LINE_KEY_BY1 = row.LINE_KEY;
    row.old_line_key = row.LINE_KEY;
    row.new_line_key = row.LINE_KEY;
    row.old_line_desc = row.LINE_DESC;
    row.new_line_desc = row.LINE_DESC;
    row.old_applicable_percent_by1 = row.APPLICABLE_PERCENT;
    row.old_raf_by1 = row.RAF;
    row.old_raf_by = row.RAF;
    row.old_adj_cy_exam = row.ADJ_CY_EXAM;
    row.old_adj_cy_consitency = row.ADJ_CY_CONSITENCY;
    row.old_adj_cy_acquisition = row.ADJ_CY_ACQUISITION;
    row.old_adj_cy_disposition = row.ADJ_CY_DISPOSITION;
    row.old_cy_annualization = row.ADJ_CY_ANNUALIZATION;
    row.new_adj_cy_exam = row.ADJ_CY_EXAM;
    row.new_adj_cy_consitency = row.ADJ_CY_CONSITENCY;
    row.new_adj_cy_acquisition = row.ADJ_CY_ACQUISITION;
    row.new_adj_cy_disposition = row.ADJ_CY_DISPOSITION;
    row.new_cy_annualization = row.ADJ_CY_ANNUALIZATION;
    row.old_cy_ptp = row.ADJ_CY_PTP;
    row.new_adj_cy_ptp = row.ADJ_CY_PTP;
    row.old_adj_total_applicable = row.ADJ_TOTAL_APPLICABLE_BY1;
    row.old_adj_total = row.ADJ_TOTAL;
    row.new_adj_total_applicable = row.ADJ_TOTAL_APPLICABLE;
    row.new_adj_total = row.ADJ_TOTAL;
    row.new_adj_desc_cy_exam = row.ADJ_CY_EXAM_D_BY1;
    row.new_adj_desc_cy_consistency = row.ADJ_CY_CONSITENCY_D_BY1;
    row.new_adj_desc_cy_acquisition = row.ADJ_CY_ACQUISITION_D_BY1;
    row.new_adj_desc_cy_disposition = row.ADJ_CY_DISPOSITION_D_BY1;
    row.new_adj_desc_cy_annualization = row.ADJ_CY_ANNUALIZATION_D_BY1;
    row.new_adj_desc_cy_ptp = row.ADJ_CY_PTP_D_BY1;
    row.old_adj_desc_cy_exam = row.ADJ_CY_EXAM_D_BY1;
    row.old_adj_desc_cy_consistency = row.ADJ_CY_CONSITENCY_D_BY1;
    row.old_adj_desc_cy_acquisition = row.ADJ_CY_ACQUISITION_D_BY1;
    row.old_adj_desc_cy_disposition = row.ADJ_CY_DISPOSITION_D_BY1;
    row.old_adj_desc_cy_annualization = row.ADJ_CY_ANNUALIZATION_D_BY1;
    row.old_adj_desc_cy_ptp = row.ADJ_CY_PTP_D_BY1;
    row.old_state = row.STATE;
    row.new_state = row.STATE;
  }

  private stampTab3LegacyFields(row: any, isNew: boolean): void {
    if (isNew) {
      Object.assign(row, {
        old_applicable_percent: 0, old_raf: 0, old_adj_cy_1: 0, old_adj_cy_exam: 0,
        old_adj_cy_consitency: 0, old_adj_cy_acquisition: 0, old_adj_cy_disposition: 0,
        old_adj_cy_annualization: 0, old_adj_total: 0, old_adj_total_applicable: 0,
        old_line_key: 0, old_line_desc: '', new_line_key: 0, new_line_desc: '',
        new_applicable_percent: 0, new_raf: 0, new_adj_cy_1: 0, new_adj_cy_exam: 0,
        new_adj_cy_consitency: 0, new_adj_cy_acquisition: 0, new_adj_cy_disposition: 0,
        new_adj_cy_annualization: 0, new_adj_total: 0, new_adj_total_applicable: 0,
        old_cy_ptp: 0, new_adj_cy_ptp: 0, new_state: '', old_state: ''
      });
      return;
    }

    row.LINE_KEY_BY2 = row.LINE_KEY;
    row.old_line_key = row.LINE_KEY;
    row.new_line_key = row.LINE_KEY;
    row.old_line_desc = row.LINE_DESC;
    row.new_line_desc = row.LINE_DESC;
    row.old_applicable_percent = row.APPLICABLE_PERCENT;
    row.old_raf = row.RAF;
    row.old_adj_cy_1 = row.ADJ_CY_1;
    row.old_adj_cy_exam = row.ADJ_CY_EXAM;
    row.old_adj_cy_consitency = row.ADJ_CY_CONSITENCY;
    row.old_adj_cy_acquisition = row.ADJ_CY_ACQUISITION;
    row.old_adj_cy_disposition = row.ADJ_CY_DISPOSITION;
    row.old_adj_cy_annualization = row.ADJ_CY_ANNUALIZATION;
    row.new_applicable_percent = row.APPLICABLE_PERCENT;
    row.new_raf = row.RAF;
    row.new_adj_cy_1 = row.ADJ_CY_1;
    row.new_adj_cy_exam = row.ADJ_CY_EXAM;
    row.new_adj_cy_consitency = row.ADJ_CY_CONSITENCY;
    row.new_adj_cy_acquisition = row.ADJ_CY_ACQUISITION;
    row.new_adj_cy_disposition = row.ADJ_CY_DISPOSITION;
    row.new_adj_cy_annualization = row.ADJ_CY_ANNUALIZATION;
    row.old_cy_ptp = row.ADJ_CY_PTP;
    row.new_adj_cy_ptp = row.ADJ_CY_PTP;
    row.old_adj_total = row.ADJ_TOTAL_BY2;
    row.old_adj_total_applicable = row.ADJ_TOTAL_APPLICABLE_BY2;
    row.new_adj_total = row.ADJ_TOTAL_BY2;
    row.new_adj_total_applicable = row.ADJ_TOTAL_APPLICABLE_BY2;
    row.new_adj_desc_cy_exam = row.ADJ_CY_EXAM_D_BY2;
    row.new_adj_desc_cy_consistency = row.ADJ_CY_CONSITENCY_D_BY2;
    row.new_adj_desc_cy_acquisition = row.ADJ_CY_ACQUISITION_D_BY2;
    row.new_adj_desc_cy_disposition = row.ADJ_CY_DISPOSITION_D_BY2;
    row.new_adj_desc_cy_annualization = row.ADJ_CY_ANNUALIZATION_D_BY2;
    row.new_adj_desc_cy_ptp = row.ADJ_CY_PTP_D_BY2;
    row.old_adj_desc_cy_exam = row.ADJ_CY_EXAM_D_BY2;
    row.old_adj_desc_cy_consistency = row.ADJ_CY_CONSITENCY_D_BY2;
    row.old_adj_desc_cy_acquisition = row.ADJ_CY_ACQUISITION_D_BY2;
    row.old_adj_desc_cy_disposition = row.ADJ_CY_DISPOSITION_D_BY2;
    row.old_adj_desc_cy_annualization = row.ADJ_CY_ANNUALIZATION_D_BY2;
    row.old_adj_desc_cy_ptp = row.ADJ_CY_PTP_D_BY2;
    row.old_state = row.STATE;
    row.new_state = row.STATE;
  }

  private stampTab4LegacyFields(row: any, isNew: boolean): void {
    if (isNew) {
      Object.assign(row, {
        old_applicable_percent: 0, old_raf: 0, old_adj_cy_2: 0, old_adj_cy_1: 0,
        old_adj_cy_exam: 0, old_adj_cy_consitency: 0, old_adj_cy_acquisition: 0,
        old_adj_cy_disposition: 0, old_adj_cy_annualization: 0, old_adj_total: 0,
        old_adj_total_applicable: 0, old_line_key: 0, old_line_desc: '',
        new_line_key: 0, new_line_desc: '', new_applicable_percent: 0, new_raf: 0,
        new_adj_cy_2: 0, new_adj_cy_1: 0, new_adj_cy_exam: 0, new_adj_cy_consitency: 0,
        new_adj_cy_acquisition: 0, new_adj_cy_disposition: 0, new_adj_cy_annualization: 0,
        new_adj_total: 0, new_adj_total_applicable: 0, old_cy_ptp: 0, new_adj_cy_ptp: 0,
        new_state: '', old_state: ''
      });
      return;
    }

    row.LINE_KEY_BY3 = row.LINE_KEY;
    row.old_line_key = row.LINE_KEY;
    row.new_line_key = row.LINE_KEY;
    row.old_line_desc = row.LINE_DESC;
    row.new_line_desc = row.LINE_DESC;
    row.old_applicable_percent = row.APPLICABLE_PERCENT;
    row.old_raf = row.RAF;
    row.old_adj_cy_2 = row.ADJ_CY_2;
    row.old_adj_cy_1 = row.ADJ_CY_1;
    row.old_adj_cy_exam = row.ADJ_CY_EXAM;
    row.old_adj_cy_consitency = row.ADJ_CY_CONSITENCY;
    row.old_adj_cy_acquisition = row.ADJ_CY_ACQUISITION;
    row.old_adj_cy_disposition = row.ADJ_CY_DISPOSITION;
    row.old_adj_cy_annualization = row.ADJ_CY_ANNUALIZATION;
    row.new_applicable_percent = row.APPLICABLE_PERCENT;
    row.new_raf = row.RAF;
    row.new_adj_cy_2 = row.ADJ_CY_2;
    row.new_adj_cy_1 = row.ADJ_CY_1;
    row.new_adj_cy_exam = row.ADJ_CY_EXAM;
    row.new_adj_cy_consitency = row.ADJ_CY_CONSITENCY;
    row.new_adj_cy_acquisition = row.ADJ_CY_ACQUISITION;
    row.new_adj_cy_disposition = row.ADJ_CY_DISPOSITION;
    row.new_adj_cy_annualization = row.ADJ_CY_ANNUALIZATION;
    row.new_adj_total = row.ADJ_TOTAL;
    row.new_adj_total_applicable = row.ADJ_TOTAL_APPLICABLE;
    row.old_adj_total = row.ADJ_TOTAL_BY3;
    row.old_adj_total_applicable = row.ADJ_TOTAL_APPLICABLE_BY3;
    row.old_cy_ptp = row.ADJ_CY_PTP;
    row.new_adj_cy_ptp = row.ADJ_CY_PTP;
    row.new_adj_desc_cy_exam = row.ADJ_CY_EXAM_D_BY3;
    row.new_adj_desc_cy_consistency = row.ADJ_CY_CONSITENCY_D_BY3;
    row.new_adj_desc_cy_acquisition = row.ADJ_CY_ACQUISITION_D_BY3;
    row.new_adj_desc_cy_disposition = row.ADJ_CY_DISPOSITION_D_BY3;
    row.new_adj_desc_cy_annualization = row.ADJ_CY_ANNUALIZATION_D_BY3;
    row.new_adj_desc_cy_ptp = row.ADJ_CY_PTP_D_BY3;
    row.old_adj_desc_cy_exam = row.ADJ_CY_EXAM_D_BY3;
    row.old_adj_desc_cy_consistency = row.ADJ_CY_CONSITENCY_D_BY3;
    row.old_adj_desc_cy_acquisition = row.ADJ_CY_ACQUISITION_D_BY3;
    row.old_adj_desc_cy_disposition = row.ADJ_CY_DISPOSITION_D_BY3;
    row.old_adj_desc_cy_annualization = row.ADJ_CY_ANNUALIZATION_D_BY3;
    row.old_adj_desc_cy_ptp = row.ADJ_CY_PTP_D_BY3;
    row.old_state = row.STATE;
    row.new_state = row.STATE;
  }

  /** Keep UI aliases and legacy uppercase API fields in sync (AngularJS tab1.html parity). */
  syncTab1TextFields(row: any): void {
    row.business_component = row.business_component ?? row.bus_component ?? row.BUSINESS_COMPONENT ?? '';
    row.bus_component = row.business_component;
    row.BUSINESS_COMPONENT = row.business_component;
    row.bc_type = row.bc_type ?? row.BC_TYPE ?? '';
    row.BC_TYPE = row.bc_type;
    row.software_type = row.software_type ?? row.SOFTWARE_TYPE ?? '';
    row.SOFTWARE_TYPE = row.software_type;
    this.cdr.markForCheck();
  }

  private syncRowLegacyFieldsForSave(row: any, tab: number): void {
    if (tab === 0) {
      this.syncTab1TextFields(row);
      row.RAF = row.RAF_CY;
      row.LINE_DESC = row.LINE_DESC_CY;
      row.APPLICABLE_PERCENT = row.APPLICABLE_PERCENT_CY;
      row.STATE_NAME = row.STATE_CY;
      row.ADJ_CY_2 = row.ADJ_CY_2_CY;
      row.ADJ_CY_1 = row.ADJ_CY_1_CY;
      row.ADJ_CY_EXAM = row.ADJ_CY_EXAM_CY;
      row.ADJ_CY_CONSITENCY = row.ADJ_CY_CONSITENCY_CY;
      row.ADJ_CY_ACQUISITION = row.ADJ_CY_ACQUISITION_CY;
      row.ADJ_CY_DISPOSITION = row.ADJ_CY_DISPOSITION_CY;
      return;
    }

    const suffix = tab === 1 ? 'BY1' : tab === 2 ? 'BY2' : 'BY3';
    row.LINE_DESC = row[`LINE_DESC_${suffix}`];
    row.RAF = row[`RAF_${suffix}`];
    row.APPLICABLE_PERCENT = row[`APPLICABLE_PERCENT_${suffix}`];
    row.STATE_NAME = row[`STATE_${suffix}`];
    row.ADJ_CY_EXAM = row[`ADJ_CY_EXAM_${suffix}`];
    row.ADJ_CY_CONSITENCY = row[`ADJ_CY_CONSITENCY_${suffix}`];
    row.ADJ_CY_ACQUISITION = row[`ADJ_CY_ACQUISITION_${suffix}`];
    row.ADJ_CY_DISPOSITION = row[`ADJ_CY_DISPOSITION_${suffix}`];
    row.ADJ_CY_ANNUALIZATION = row[`ADJ_CY_ANNUALIZATION_${suffix}`];
    row.ADJ_CY_PTP = row[`ADJ_CY_PTP_${suffix}`];
    row.ADJ_DESC_CY_EXAM = row[`ADJ_CY_EXAM_D_${suffix}`];
    row.ADJ_DESC_CY_CONSITENCY = row[`ADJ_CY_CONSITENCY_D_${suffix}`];
    row.ADJ_DESC_CY_ACQUISITION = row[`ADJ_CY_ACQUISITION_D_${suffix}`];
    row.ADJ_DESC_CY_DISPOSITION = row[`ADJ_CY_DISPOSITION_D_${suffix}`];
    row.ADJ_DESC_CY_ANNUALIZATION = row[`ADJ_CY_ANNUALIZATION_D_${suffix}`];
    if (tab === 2) {
      row.ADJ_CY_1 = row.ADJ_CY_1_BY2;
    }
    if (tab === 3) {
      row.ADJ_CY_1 = row.ADJ_CY_1_BY3;
      row.ADJ_CY_2 = row.ADJ_CY_2_BY3;
    }
  }

  private cleanRowForSave(row: any, tab: number): any {
    this.syncRowLegacyFieldsForSave(row, tab);
    const cleaned = { ...row };
    delete cleaned.open;
    return cleaned;
  }

  onScheduleTabChange(index: number): void {
    this.activeScheduleTab = index;
    this.cdr.detectChanges();
  }

  onTabChange(index: number): void {
    this.selectedTab = index;
    this.hideCopy = index === 0;
    this.copyValue = '';
    this.updateCopyOptions();
    this.cdr.detectChanges();
  }

  private updateCopyOptions(): void {
    if (this.selectedTab === 1) {
      this.templateData = [this.yearTabs[2], this.yearTabs[3]];
    } else if (this.selectedTab === 2) {
      this.templateData = [this.yearTabs[1], this.yearTabs[3]];
    } else if (this.selectedTab === 3) {
      this.templateData = [this.yearTabs[1], this.yearTabs[2]];
    } else {
      this.templateData = [];
    }
    if (this.templateData.length && !this.copyValue) {
      this.copyValue = this.templateData[0];
    }
  }

  onCopyValueChange(value: string): void {
    if (!value) return;
    if (this.selectedTab === 1) {
      if (value === this.yearTabs[2]) this.copyTabData(this.tab3Rows, 'BY2', this.tab2Rows, 'BY1', 'display_dropdown_tab2');
      if (value === this.yearTabs[3]) this.copyTabData(this.tab4Rows, 'BY3', this.tab2Rows, 'BY1', 'display_dropdown_tab2');
    } else if (this.selectedTab === 2) {
      if (value === this.yearTabs[1]) this.copyTabData(this.tab2Rows, 'BY1', this.tab3Rows, 'BY2', 'display_dropdown_tab3');
      if (value === this.yearTabs[3]) this.copyTabData(this.tab4Rows, 'BY3', this.tab3Rows, 'BY2', 'display_dropdown_tab3');
    } else if (this.selectedTab === 3) {
      if (value === this.yearTabs[1]) this.copyTabData(this.tab2Rows, 'BY1', this.tab4Rows, 'BY3', 'display_dropdown_tab4', true);
      if (value === this.yearTabs[2]) this.copyTabData(this.tab3Rows, 'BY2', this.tab4Rows, 'BY3', 'display_dropdown_tab4', false, true);
    }
    this.recalculateSummary();
    this.cdr.detectChanges();
  }

  private copyTabData(
    sourceRows: any[],
    sourceSuffix: TabSuffix,
    targetRef: any[],
    targetSuffix: TabSuffix,
    dropdownFlag: string,
    resetCy2 = false,
    copyCy1ToCy2 = false
  ): void {
    const copied = JSON.parse(JSON.stringify(sourceRows));
    copied.forEach((row: any) => {
      row[dropdownFlag] = false;
      row.open = false;
      row[`TAB_YEAR_${targetSuffix}`] = targetSuffix;
      row[`MAP_KEY_${targetSuffix}`] = '';
      row[`LINE_DESC_${targetSuffix}`] = row[`LINE_DESC_${sourceSuffix}`] || '';
      row[`APPLICABLE_PERCENT_${targetSuffix}`] = row[`APPLICABLE_PERCENT_${sourceSuffix}`] || 0;
      row[`RAF_${targetSuffix}`] = row[`RAF_${sourceSuffix}`] || 0;
      row[`ADJ_CY_1_${targetSuffix}`] = row[`ADJ_CY_1_${sourceSuffix}`] || 0;
      row[`ADJ_CY_EXAM_${targetSuffix}`] = row[`ADJ_CY_EXAM_${sourceSuffix}`] || 0;
      row[`ADJ_CY_CONSITENCY_${targetSuffix}`] = row[`ADJ_CY_CONSITENCY_${sourceSuffix}`] || 0;
      row[`ADJ_CY_ACQUISITION_${targetSuffix}`] = row[`ADJ_CY_ACQUISITION_${sourceSuffix}`] || 0;
      row[`ADJ_CY_DISPOSITION_${targetSuffix}`] = row[`ADJ_CY_DISPOSITION_${sourceSuffix}`] || 0;
      row[`ADJ_CY_ANNUALIZATION_${targetSuffix}`] = row[`ADJ_CY_ANNUALIZATION_${sourceSuffix}`] || 0;
      row[`ADJ_CY_PTP_${targetSuffix}`] = row[`ADJ_CY_PTP_${sourceSuffix}`] || 0;
      row[`STATE_${targetSuffix}`] = row[`STATE_${sourceSuffix}`] || '';
      row[`INCLUDE_IN_CALC_${targetSuffix}`] = row[`INCLUDE_IN_CALC_${sourceSuffix}`] || '';
      row[`ADJ_CY_EXAM_D_${targetSuffix}`] = row[`ADJ_CY_EXAM_D_${sourceSuffix}`] || '';
      row[`ADJ_CY_CONSITENCY_D_${targetSuffix}`] = row[`ADJ_CY_CONSITENCY_D_${sourceSuffix}`] || '';
      row[`ADJ_CY_ACQUISITION_D_${targetSuffix}`] = row[`ADJ_CY_ACQUISITION_D_${sourceSuffix}`] || '';
      row[`ADJ_CY_DISPOSITION_D_${targetSuffix}`] = row[`ADJ_CY_DISPOSITION_D_${sourceSuffix}`] || '';
      row[`ADJ_CY_ANNUALIZATION_D_${targetSuffix}`] = row[`ADJ_CY_ANNUALIZATION_D_${sourceSuffix}`] || '';
      if (resetCy2) row.ADJ_CY_2_BY3 = 0;
      if (copyCy1ToCy2) row.ADJ_CY_2_BY3 = row.ADJ_CY_1_BY3;
      if (targetSuffix === 'BY1') this.recalculateBy1Row(row);
      if (targetSuffix === 'BY2') this.recalculateBy2Row(row);
      if (targetSuffix === 'BY3') this.recalculateBy3Row(row);
    });

    if (targetSuffix === 'BY1') this.tab2Rows = copied;
    if (targetSuffix === 'BY2') this.tab3Rows = copied;
    if (targetSuffix === 'BY3') this.tab4Rows = copied;
  }

  toggleRowDetails(row: any): void {
    row.open = !row.open;
    this.cdr.detectChanges();
  }

  addRow(): void {
    if (this.selectedTab === 0) {
      const row: any = { TYPE: 'NEW' };
      this.normalizeTab1Row(row, true);
      this.tab1Rows = [...this.tab1Rows, row];
    } else if (this.selectedTab === 1) {
      const row: any = { TYPE: 'NEW' };
      this.normalizeTab2Row(row, true);
      this.tab2Rows = [...this.tab2Rows, row];
    } else if (this.selectedTab === 2) {
      const row: any = { TYPE: 'NEW' };
      this.normalizeTab3Row(row, true);
      this.tab3Rows = [...this.tab3Rows, row];
    } else {
      const row: any = { TYPE: 'NEW' };
      this.normalizeTab4Row(row, true);
      this.tab4Rows = [...this.tab4Rows, row];
    }
    this.noRecords = false;
    this.cdr.detectChanges();
  }

  removeRow(index: number): void {
    const rows = this.getRowsByTab(this.selectedTab);
    const row = rows[index];
    if (!row) return;

    if (row.TYPE !== 'NEW') {
      const deleteRow: any = { ...row, type: 'D' };
      if (this.selectedTab === 0) {
        deleteRow.new_tab_year = row.TAB_YEAR_CY;
        deleteRow.new_map_key = row.MAP_KEY_CY;
        deleteRow.new_raf = row.RAF_CY;
      } else if (this.selectedTab === 1) {
        deleteRow.new_tab_year = row.TAB_YEAR_BY1;
        deleteRow.new_map_key = row.MAP_KEY_BY1;
        deleteRow.new_raf = row.RAF_BY1;
      } else if (this.selectedTab === 2) {
        deleteRow.new_tab_year = row.TAB_YEAR_BY2;
        deleteRow.new_map_key = row.MAP_KEY_BY2;
        deleteRow.new_raf = row.RAF_BY2;
      } else {
        deleteRow.new_tab_year = row.TAB_YEAR_BY3;
        deleteRow.new_map_key = row.MAP_KEY_BY3;
        deleteRow.new_raf = row.RAF_BY3;
      }
      this.deletedRows.push(deleteRow);
    }

    rows.splice(index, 1);
    this.recalculateSummary();
    this.noRecords = !this.tab1Rows.length && !this.tab2Rows.length && !this.tab3Rows.length && !this.tab4Rows.length;
    this.cdr.detectChanges();
  }

  onLineChange(row: any, suffix: TabSuffix): void {
    const lineDesc = row[`LINE_DESC_${suffix}`];
    const line = this.lineOptions.find((item: any) => item.LINE_DESCRIPTION === lineDesc);
    if (line) {
      row[`APPLICABLE_PERCENT_${suffix}`] = Number(line.APPLICABLE_PERCENT || 0);
      row[`INCLUDE_IN_CALC_${suffix}`] = line.INCLUDE_IN_CALC || '';
    }
    if (suffix === 'CY') this.onReturnAsQRE(-1, row);
    if (suffix === 'BY1') this.recalculateBy1Row(row);
    if (suffix === 'BY2') this.recalculateBy2Row(row);
    if (suffix === 'BY3') this.recalculateBy3Row(row);
    this.recalculateSummary();
    this.cdr.detectChanges();
  }

  onReturnAsQRE(_index: number, row: any): void {
    row.ADJ_TOTAL_CY = (Number(row.RAF_CY || 0) * Number(row.APPLICABLE_PERCENT_CY || 0)) / 100;
    row.ADJ_TOTAL_APPLICABLE_CY = row.ADJ_TOTAL_CY;
    this.recalculateSummary();
    this.cdr.detectChanges();
  }

  onBaseYearChange(_index: number, row: any, suffix: TabSuffix): void {
    if (suffix === 'BY1') this.recalculateBy1Row(row);
    if (suffix === 'BY2') this.recalculateBy2Row(row);
    if (suffix === 'BY3') this.recalculateBy3Row(row);
    this.recalculateSummary();
    this.cdr.detectChanges();
  }

  private recalculateBy1Row(row: any): void {
    row.ADJ_TOTAL_BY1 = Number(row.ADJ_CY_EXAM_BY1 || 0) + Number(row.ADJ_CY_ACQUISITION_BY1 || 0)
      + Number(row.ADJ_CY_CONSITENCY_BY1 || 0) + Number(row.ADJ_CY_DISPOSITION_BY1 || 0)
      + Number(row.ADJ_CY_ANNUALIZATION_BY1 || 0) + Number(row.ADJ_CY_PTP_BY1 || 0);
    row.ADJ_TOTAL_APPLICABLE_BY1 = (row.ADJ_TOTAL_BY1 * Number(row.APPLICABLE_PERCENT_BY1 || 0)) / 100;
    row.ADJ_TOTAL_SUMMARY_BY1 = row.ADJ_TOTAL_BY1 + Number(row.RAF_BY1 || 0);
    row.ADJ_TOTAL_APPLICABLE_SUMMARY_BY1 = (row.ADJ_TOTAL_SUMMARY_BY1 * Number(row.APPLICABLE_PERCENT_BY1 || 0)) / 100;
  }

  private recalculateBy2Row(row: any): void {
    row.ADJ_TOTAL_BY2 = Number(row.ADJ_CY_EXAM_BY2 || 0) + Number(row.ADJ_CY_ACQUISITION_BY2 || 0)
      + Number(row.ADJ_CY_CONSITENCY_BY2 || 0) + Number(row.ADJ_CY_DISPOSITION_BY2 || 0)
      + Number(row.ADJ_CY_ANNUALIZATION_BY2 || 0) + Number(row.ADJ_CY_PTP_BY2 || 0);
    row.ADJ_TOTAL_APPLICABLE_BY2 = (row.ADJ_TOTAL_BY2 * Number(row.APPLICABLE_PERCENT_BY2 || 0)) / 100;
    row.ADJ_TOTAL_SUMMARY_BY2 = row.ADJ_TOTAL_BY2 + Number(row.RAF_BY2 || 0) + Number(row.ADJ_CY_1_BY2 || 0);
    row.ADJ_TOTAL_APPLICABLE_SUMMARY_BY2 = (row.ADJ_TOTAL_SUMMARY_BY2 * Number(row.APPLICABLE_PERCENT_BY2 || 0)) / 100;
  }

  private recalculateBy3Row(row: any): void {
    row.ADJ_TOTAL_BY3 = Number(row.ADJ_CY_EXAM_BY3 || 0) + Number(row.ADJ_CY_CONSITENCY_BY3 || 0)
      + Number(row.ADJ_CY_ACQUISITION_BY3 || 0) + Number(row.ADJ_CY_DISPOSITION_BY3 || 0)
      + Number(row.ADJ_CY_ANNUALIZATION_BY3 || 0) + Number(row.ADJ_CY_PTP_BY3 || 0);
    row.ADJ_TOTAL_APPLICABLE_BY3 = (row.ADJ_TOTAL_BY3 * Number(row.APPLICABLE_PERCENT_BY3 || 0)) / 100;
    row.ADJ_TOTAL_SUMMARY_BY3 = row.ADJ_TOTAL_BY3 + Number(row.RAF_BY3 || 0)
      + Number(row.ADJ_CY_1_BY3 || 0) + Number(row.ADJ_CY_2_BY3 || 0);
    row.ADJ_TOTAL_APPLICABLE_SUMMARY_BY3 = (row.ADJ_TOTAL_SUMMARY_BY3 * Number(row.APPLICABLE_PERCENT_BY3 || 0)) / 100;
  }

  private recalculateSummary(): void {
    this.tab1Rows.forEach((row: any) => {
      row.ADJ_TOTAL_CY = (Number(row.RAF_CY || 0) * Number(row.APPLICABLE_PERCENT_CY || 0)) / 100;
    });
    this.tab2Rows.forEach((row: any) => this.recalculateBy1Row(row));
    this.tab3Rows.forEach((row: any) => this.recalculateBy2Row(row));
    this.tab4Rows.forEach((row: any) => this.recalculateBy3Row(row));

    const summary = this.createEmptySummary();
    this.tab1Rows.forEach((row: any) => {
      if (row.INCLUDE_IN_CALC_CY === 'Y') {
        summary.CURRENT_YEAR_CQRE += Number(row.RAF_CY || 0);
        summary.CURRENT_YEAR_CQRE_Y += Number(row.ADJ_TOTAL_CY || 0);
      }
    });
    this.tab2Rows.forEach((row: any) => {
      if (row.INCLUDE_IN_CALC_BY1 === 'Y') {
        summary.CURRENT_one_QRE += Number(row.ADJ_TOTAL_SUMMARY_BY1 || 0);
        summary.CURRENT_one_QRE_Y += Number(row.ADJ_TOTAL_APPLICABLE_SUMMARY_BY1 || 0);
      }
    });
    this.tab3Rows.forEach((row: any) => {
      if (row.INCLUDE_IN_CALC_BY2 === 'Y') {
        summary.CURRENT_two_QRE += Number(row.ADJ_TOTAL_SUMMARY_BY2 || 0);
        summary.CURRENT_two_QRE_Y += Number(row.ADJ_TOTAL_APPLICABLE_SUMMARY_BY2 || 0);
      }
    });
    this.tab4Rows.forEach((row: any) => {
      if (row.INCLUDE_IN_CALC_BY3 === 'Y') {
        summary.CURRENT_three_QRE += Number(row.ADJ_TOTAL_SUMMARY_BY3 || 0);
        summary.CURRENT_three_QRE_Y += Number(row.ADJ_TOTAL_APPLICABLE_SUMMARY_BY3 || 0);
      }
    });

    summary.CURRENT_TOTAL_QRE = summary.CURRENT_YEAR_CQRE + summary.CURRENT_one_QRE
      + summary.CURRENT_two_QRE + summary.CURRENT_three_QRE;
    summary.CURRENT_TOTAL_QRE_Y = summary.CURRENT_YEAR_CQRE_Y + summary.CURRENT_one_QRE_Y
      + summary.CURRENT_two_QRE_Y + summary.CURRENT_three_QRE_Y;
    this.summary = summary;
  }

  private getRowsByTab(tab: number): any[] {
    if (tab === 0) return this.tab1Rows;
    if (tab === 1) return this.tab2Rows;
    if (tab === 2) return this.tab3Rows;
    return this.tab4Rows;
  }

  private validateBeforeSave(): boolean {
    for (const row of this.tab1Rows) {
      if (!row.LINE_DESC_CY) {
        this.gtwSnackbarService.openSnackBar('Please Select Form 6765 Line', 'gtw-snackbar--yellow');
        return false;
      }
      if (!row.STATE_CY) {
        this.gtwSnackbarService.openSnackBar('Please Select State', 'gtw-snackbar--yellow');
        return false;
      }
    }
    for (const row of this.tab2Rows) {
      if (!row.LINE_DESC_BY1) {
        this.gtwSnackbarService.openSnackBar('Please Select Form 6765 Line', 'gtw-snackbar--yellow');
        return false;
      }
    }
    for (const row of this.tab3Rows) {
      if (!row.LINE_DESC_BY2) {
        this.gtwSnackbarService.openSnackBar('Please Select Form 6765 Line', 'gtw-snackbar--yellow');
        return false;
      }
    }
    for (const row of this.tab4Rows) {
      if (!row.LINE_DESC_BY3) {
        this.gtwSnackbarService.openSnackBar('Please Select Form 6765 Line', 'gtw-snackbar--yellow');
        return false;
      }
    }
    return true;
  }

  private prepareSavePayload(): any[] {
    const payload: any[] = [];

    this.tab1Rows.forEach((row: any) => {
      const line = this.lineOptions.find((item: any) => item.LINE_DESCRIPTION === row.LINE_DESC_CY);
      const state = this.stateOptions.find((item: any) => item.STATE_NAME === row.STATE_CY);
      const cleaned = this.cleanRowForSave(row, 0);
      const businessComponent = cleaned.business_component ?? cleaned.BUSINESS_COMPONENT ?? '';
      const bcType = cleaned.bc_type ?? cleaned.BC_TYPE ?? '';
      const softwareType = cleaned.software_type ?? cleaned.SOFTWARE_TYPE ?? '';
      payload.push({
        ...cleaned,
        type: 'E',
        new_line_key: line?.LINE_ACCT_REFERENCE_KEY || row.LINE_KEY || 0,
        new_line_desc: row.LINE_DESC_CY,
        new_applicable_percent: row.APPLICABLE_PERCENT_CY,
        new_raf: row.RAF_CY,
        new_adj_cy_2: row.ADJ_CY_2_CY,
        new_adj_cy_1: row.ADJ_CY_1_CY,
        new_adj_cy_exam: row.ADJ_CY_EXAM_CY,
        new_adj_cy_consitency: row.ADJ_CY_CONSITENCY_CY,
        new_adj_cy_acquisition: row.ADJ_CY_ACQUISITION_CY,
        new_adj_cy_disposition: row.ADJ_CY_DISPOSITION_CY,
        new_adj_total: row.ADJ_TOTAL_CY,
        new_adj_total_applicable: row.ADJ_TOTAL_APPLICABLE_CY,
        BUSINESS_COMPONENT: businessComponent,
        BC_TYPE: bcType,
        SOFTWARE_TYPE: softwareType,
        business_component: businessComponent,
        new_business_component: businessComponent,
        new_bc_type: bcType,
        new_software_type: softwareType,
        new_tab_year: row.TAB_YEAR_CY,
        new_map_key: row.MAP_KEY_CY,
        new_state: state?.STATE || row.STATE || ''
      });
    });

    this.tab2Rows.forEach((row: any) => {
      const line = this.lineOptions.find((item: any) => item.LINE_DESCRIPTION === row.LINE_DESC_BY1);
      const state = this.stateOptions.find((item: any) => item.STATE_NAME === row.STATE_BY1);
      payload.push({
        ...this.cleanRowForSave(row, 1),
        type: 'E',
        new_line_key: line?.LINE_ACCT_REFERENCE_KEY || row.LINE_KEY || 0,
        new_line_desc: row.LINE_DESC_BY1,
        new_applicable_percent: row.APPLICABLE_PERCENT_BY1,
        new_raf: row.RAF_BY1,
        new_adj_cy_2: row.ADJ_CY_2_BY1,
        new_adj_cy_1: row.ADJ_CY_1_BY1,
        new_adj_cy_exam: row.ADJ_CY_EXAM_BY1,
        new_adj_cy_consitency: row.ADJ_CY_CONSITENCY_BY1,
        new_adj_cy_acquisition: row.ADJ_CY_ACQUISITION_BY1,
        new_adj_cy_disposition: row.ADJ_CY_DISPOSITION_BY1,
        new_adj_cy_ptp: row.ADJ_CY_PTP_BY1,
        new_adj_cy_annualization: row.ADJ_CY_ANNUALIZATION_BY1,
        new_adj_total: row.ADJ_TOTAL_BY1,
        new_adj_total_applicable: row.ADJ_TOTAL_APPLICABLE_BY1,
        new_adj_desc_cy_exam: row.ADJ_CY_EXAM_D_BY1,
        new_adj_desc_cy_consistency: row.ADJ_CY_CONSITENCY_D_BY1,
        new_adj_desc_cy_acquisition: row.ADJ_CY_ACQUISITION_D_BY1,
        new_adj_desc_cy_disposition: row.ADJ_CY_DISPOSITION_D_BY1,
        new_adj_desc_cy_annualization: row.ADJ_CY_ANNUALIZATION_D_BY1,
        new_adj_desc_cy_ptp: row.ADJ_CY_PTP_D_BY1,
        new_tab_year: row.TAB_YEAR_BY1,
        new_map_key: row.MAP_KEY_BY1,
        new_state: state?.STATE || row.STATE || ''
      });
    });

    this.tab3Rows.forEach((row: any) => {
      const line = this.lineOptions.find((item: any) => item.LINE_DESCRIPTION === row.LINE_DESC_BY2);
      const state = this.stateOptions.find((item: any) => item.STATE_NAME === row.STATE_BY2);
      payload.push({
        ...this.cleanRowForSave(row, 2),
        type: 'E',
        new_line_key: line?.LINE_ACCT_REFERENCE_KEY || row.LINE_KEY || 0,
        new_line_desc: row.LINE_DESC_BY2,
        new_applicable_percent: row.APPLICABLE_PERCENT_BY2,
        new_raf: row.RAF_BY2,
        new_adj_cy_1: row.ADJ_CY_1_BY2,
        new_adj_cy_exam: row.ADJ_CY_EXAM_BY2,
        new_adj_cy_consitency: row.ADJ_CY_CONSITENCY_BY2,
        new_adj_cy_acquisition: row.ADJ_CY_ACQUISITION_BY2,
        new_adj_cy_disposition: row.ADJ_CY_DISPOSITION_BY2,
        new_adj_cy_ptp: row.ADJ_CY_PTP_BY2,
        new_adj_cy_annualization: row.ADJ_CY_ANNUALIZATION_BY2,
        new_adj_total: row.ADJ_TOTAL_BY2,
        new_adj_total_applicable: row.ADJ_TOTAL_APPLICABLE_BY2,
        new_adj_desc_cy_exam: row.ADJ_CY_EXAM_D_BY2,
        new_adj_desc_cy_consistency: row.ADJ_CY_CONSITENCY_D_BY2,
        new_adj_desc_cy_acquisition: row.ADJ_CY_ACQUISITION_D_BY2,
        new_adj_desc_cy_disposition: row.ADJ_CY_DISPOSITION_D_BY2,
        new_adj_desc_cy_annualization: row.ADJ_CY_ANNUALIZATION_D_BY2,
        new_adj_desc_cy_ptp: row.ADJ_CY_PTP_D_BY2,
        new_tab_year: row.TAB_YEAR_BY2,
        new_map_key: row.MAP_KEY_BY2,
        new_state: state?.STATE || row.STATE || ''
      });
    });

    this.tab4Rows.forEach((row: any) => {
      const line = this.lineOptions.find((item: any) => item.LINE_DESCRIPTION === row.LINE_DESC_BY3);
      const state = this.stateOptions.find((item: any) => item.STATE_NAME === row.STATE_BY3);
      payload.push({
        ...this.cleanRowForSave(row, 3),
        type: 'E',
        new_line_key: line?.LINE_ACCT_REFERENCE_KEY || row.LINE_KEY || 0,
        new_line_desc: row.LINE_DESC_BY3,
        new_applicable_percent: row.APPLICABLE_PERCENT_BY3,
        new_raf: row.RAF_BY3,
        new_adj_cy_1: row.ADJ_CY_1_BY3,
        new_adj_cy_2: row.ADJ_CY_2_BY3,
        new_adj_cy_exam: row.ADJ_CY_EXAM_BY3,
        new_adj_cy_consitency: row.ADJ_CY_CONSITENCY_BY3,
        new_adj_cy_acquisition: row.ADJ_CY_ACQUISITION_BY3,
        new_adj_cy_disposition: row.ADJ_CY_DISPOSITION_BY3,
        new_adj_cy_ptp: row.ADJ_CY_PTP_BY3,
        new_adj_cy_annualization: row.ADJ_CY_ANNUALIZATION_BY3,
        new_adj_total: row.ADJ_TOTAL_BY3,
        new_adj_total_applicable: row.ADJ_TOTAL_APPLICABLE_BY3,
        new_adj_desc_cy_exam: row.ADJ_CY_EXAM_D_BY3,
        new_adj_desc_cy_consistency: row.ADJ_CY_CONSITENCY_D_BY3,
        new_adj_desc_cy_acquisition: row.ADJ_CY_ACQUISITION_D_BY3,
        new_adj_desc_cy_disposition: row.ADJ_CY_DISPOSITION_D_BY3,
        new_adj_desc_cy_annualization: row.ADJ_CY_ANNUALIZATION_D_BY3,
        new_adj_desc_cy_ptp: row.ADJ_CY_PTP_D_BY3,
        new_tab_year: row.TAB_YEAR_BY3,
        new_map_key: row.MAP_KEY_BY3,
        new_state: state?.STATE || row.STATE || ''
      });
    });

    return [...payload, ...this.deletedRows];
  }

  save(): void {
    if (this.crudLoading || this.isSaveClicked) {
      this.gtwSnackbarService.openSnackBar('Please wait while we save this request', 'gtw-snackbar--yellow');
      return;
    }
    if (!this.validateBeforeSave()) return;

    this.isSaveClicked = true;
    this.crudLoading = true;
    this.cdr.detectChanges();

    const jsonObj = this.prepareSavePayload();
    const jsonSettings = {
      tax_year: this.filterParams?.tax_year || this.globalService?.globalParams?.tax_year,
      scenario: this.filterParams?.scenario || this.globalService?.globalParams?.scenario,
      jcd_key: this.filterParams?.jcd_key || this.globalService?.globalParams?.jcd_key,
      combination_key: this.rowData?.CY_COMBINATION_KEY
    };

    this.qreEntityDetailsService.saveData(
      this.GENERAL_CONFIG.base_url,
      'wdbtx0',
      jsonObj,
      jsonSettings,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        if (result?.errorMessage && result.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(result.errorMessage, 'gtw-snackbar--red');
        } else {
          this.gtwSnackbarService.openSnackBar('Data has been saved successfully', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      () => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while saving', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  reset(): void {
    this.isInitialized = false;
    this.deletedRows = [];
    this.summary = this.createEmptySummary();
    this.showFormLoader = true;
    this.tryInit();
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
